const REDITUS_TRACKING_SCRIPT_ID = 'reditus-tracking-script'
const REDITUS_TRACKING_SCRIPT_URL = 'https://script.getreditus.com/v2.js'

type ReditusWidgetPayload = {
  token: string
  productId: string
  user: {
    uid: string
    email: string
    companyName: string
    shopifyDomain: string
  }
}

type ReditusWindow = Window & {
  gr?: ((command: string, payload?: Record<string, unknown>) => void) & {
    init?: (payload: Record<string, unknown>) => void
    open?: () => void
  }
  referralWidget?: {
    init?: (payload: Record<string, unknown>) => void
    open?: () => void
    show?: () => void
  }
}

let scriptLoadingPromise: Promise<void> | null = null
let initializedShopUid: string | null = null

const waitForReditusTrackerReady = async (timeoutMs = 5000): Promise<void> => {
  if (typeof window === 'undefined') return

  const startedAt = Date.now()

  await new Promise<void>((resolve, reject) => {
    const tick = () => {
      const gr = (window as ReditusWindow).gr as A
      const hasLoadedTracker = typeof gr === 'function' && !Array.isArray(gr?.q)

      if (hasLoadedTracker) {
        resolve()
        return
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Reditus tracker did not become ready in time'))
        return
      }

      window.setTimeout(tick, 100)
    }

    tick()
  })
}

export const loadReditusScript = async (): Promise<void> => {
  if (typeof window === 'undefined') return

  const win = window as ReditusWindow
  if (typeof win.gr !== 'function') {
    const queued = ((...args: unknown[]) => {
      ;(queued as A).q = (queued as A).q || []
      ;(queued as A).q.push(args)
    }) as unknown as ReditusWindow['gr']
    ;(queued as A).ce = 60
    win.gr = queued
  }

  const existingById = document.getElementById(REDITUS_TRACKING_SCRIPT_ID) as HTMLScriptElement | null
  if (existingById) {
    await waitForReditusTrackerReady()
    return
  }

  const existingBySrc = document.querySelector(
    `script[src*="${REDITUS_TRACKING_SCRIPT_URL}"]`,
  ) as HTMLScriptElement | null
  if (existingBySrc) {
    if (!existingBySrc.id) existingBySrc.id = REDITUS_TRACKING_SCRIPT_ID
    await waitForReditusTrackerReady()
    return
  }
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = REDITUS_TRACKING_SCRIPT_ID
    script.src = REDITUS_TRACKING_SCRIPT_URL
    script.async = true
    script.onload = () => {
      console.info('[Reditus] tracking script loaded')
      resolve()
    }
    script.onerror = () => {
      console.info('[Reditus] tracking script failed to load')
      reject(new Error('Failed to load Reditus tracking script'))
    }
    document.body.appendChild(script)
  })

  await scriptLoadingPromise
  await waitForReditusTrackerReady()
}

export const initReditusWidget = async (payload: ReditusWidgetPayload): Promise<void> => {
  await loadReditusScript()

  if (!payload?.productId || !payload?.token) {
    throw new Error('Reditus init payload is missing productId/token')
  }

  if (initializedShopUid === payload.user.uid) return

  const win = window as ReditusWindow
  const gr = win.gr
  const widget = win.referralWidget

  if (typeof gr === 'function') {
    ensureReferralWidgetCookie(payload)
    gr('initCustomer', payload.user.uid)
    gr('track', 'pageview')
    gr('loadReferralWidget', {
      productId: payload.productId,
      product_id: payload.productId,
      token: payload.token,
      auth_token: payload.token,
      user_details: {
        email: payload.user.email,
        companyId: payload.user.uid,
        company_id: payload.user.uid,
        companyName: payload.user.companyName,
        company_name: payload.user.companyName,
      },
    })

    await waitForReferralWidget()
    initializedShopUid = payload.user.uid
    return
  }

  if (widget?.init) {
    widget.init({
      auth: payload.token,
      token: payload.token,
      productId: payload.productId,
      pid: payload.productId,
      uid: payload.user.uid,
      email: payload.user.email,
      companyName: payload.user.companyName,
      shopifyDomain: payload.user.shopifyDomain,
    })
    initializedShopUid = payload.user.uid
    return
  }

  throw new Error('Reditus widget API is unavailable: missing window.gr loadReferralWidget')
}

export const openReditusWidget = (): void => {
  const win = window as ReditusWindow
  const widget = win.referralWidget

  if (widget?.show) {
    widget.show()
    return
  }

  if (widget?.open) {
    widget.open()
    return
  }

  if ((win.gr as { open?: () => void } | undefined)?.open) {
    ;(win.gr as { open?: () => void }).open?.()
    return
  }

  if (!widget) {
    throw new Error('Reditus widget is not ready')
  }
}

const waitForReferralWidget = async (timeoutMs = 5000): Promise<void> => {
  const startedAt = Date.now()

  await new Promise<void>((resolve, reject) => {
    const tick = () => {
      const win = window as ReditusWindow
      if (win.referralWidget) {
        resolve()
        return
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Reditus referral widget failed to initialize'))
        return
      }

      window.setTimeout(tick, 100)
    }

    tick()
  })
}

const ensureReferralWidgetCookie = (payload: ReditusWidgetPayload): void => {
  const cookiePayload = {
    product_id: payload.productId,
    auth_token: payload.token,
    company_id: payload.user.uid,
    company_name: payload.user.companyName,
    email: payload.user.email,
  }

  const encoded = encodeURIComponent(JSON.stringify(cookiePayload))
  const expires = new Date(Date.now() + 30 * 24 * 3600 * 1000).toUTCString()
  document.cookie = `_gr_referral_widget=${encoded}; expires=${expires}; path=/; SameSite=None; Secure`
}
